import {
  getFirestore,
  doc,
  getDoc,
  collection,
  addDoc,
  setDoc,
  onSnapshot,
  query,
  where,
  orderBy,
  limit,
  getDocs,
  deleteDoc,
  Timestamp,
  QuerySnapshot,
  DocumentData,
  Firestore,
  CollectionReference,
} from 'firebase/firestore';
import { PublicForm } from '../models/PublicForm';
import { Channel } from '../models/Channel';
import { Message } from '../models/Message';

// Helper functions to handle paths safely with proper typing
function getCollectionRef(db: Firestore, path: string[]): CollectionReference {
  // Handle different path lengths explicitly
  switch (path.length) {
    case 1:
      return collection(db, path[0]);
    case 2:
      return collection(db, path[0], path[1]);
    case 3:
      return collection(db, path[0], path[1], path[2]);
    case 4:
      return collection(db, path[0], path[1], path[2], path[3]);
    case 5:
      return collection(db, path[0], path[1], path[2], path[3], path[4]);
    case 6:
      return collection(db, path[0], path[1], path[2], path[3], path[4], path[5]);
    default:
      throw new Error(`Unsupported path length: ${path.length}`);
  }
}

function getDocRef(db: Firestore, path: string[]): any {
  // Handle different path lengths explicitly
  switch (path.length) {
    case 2:
      return doc(db, path[0], path[1]);
    case 3:
      return doc(db, path[0], path[1], path[2]);
    case 4:
      return doc(db, path[0], path[1], path[2], path[3]);
    case 5:
      return doc(db, path[0], path[1], path[2], path[3], path[4]);
    case 6:
      return doc(db, path[0], path[1], path[2], path[3], path[4], path[5]);
    case 7:
      return doc(db, path[0], path[1], path[2], path[3], path[4], path[5], path[6]);
    default:
      throw new Error(`Unsupported doc path length: ${path.length}`);
  }
}

class FirestoreRepository {
  private db: Firestore;
  
  constructor() {
    try {
      this.db = getFirestore();
    } catch (error) {
      console.error('Error getting Firestore instance:', error);
      throw error;
    }
  }

  // Form operations
  public async getPublicForm(formId: string): Promise<PublicForm | null> {
    try {
      console.log('🚀 FirestoreRepository.getPublicForm called with formId:', formId);
      console.log('🔧 Firestore instance available:', !!this.db);
      console.log('🏗️ Firestore app:', this.db.app.name, this.db.app.options.projectId);
      // Try different paths to find the form
      const possiblePaths = [
        `public_forms/${formId}`,
        `forms/${formId}`,
        `channels/forms/${formId}`
      ];
      
      // If the ID doesn't already have the prefix, also try with it
      if (!formId.startsWith('Public_Form_')) {
        possiblePaths.push(`public_forms/Public_Form_${formId}`);
      }
      
      console.log('Attempting to load form with ID:', formId);
      console.log('Trying these paths:', possiblePaths);
      
      // Try each path until we find the document
      for (const path of possiblePaths) {
        console.log('🔍 Checking path:', path);
        try {
          const docRef = doc(this.db, path);
          console.log('📄 Document reference created for path:', path);
          const docSnap = await getDoc(docRef);
          console.log('📊 Document snapshot obtained, exists:', docSnap.exists());
          
          if (docSnap.exists()) {
            console.log('✅ Document exists at path:', path);
            const data = docSnap.data();
            console.log('📋 Raw document data:', JSON.stringify(data, null, 2));
            console.log('🔍 Data keys:', Object.keys(data));
            console.log('🏷️ Title field:', data.title);
            console.log('🔗 ChannelUid field:', data.channelUid);
            console.log('🤖 AI field:', data.AI);
            console.log('📝 Questions field type:', typeof data.questions);
            console.log('💬 IntroMessage field:', data.introMessage);
            
            // Check if this is a valid form document - be more lenient
            if (!data.title && !data.channelUid && !data.AI && !data.questions) {
              console.log('❌ Document exists but does not appear to be a form (no title, channelUid, AI, or questions), skipping');
              console.log('📊 Available fields:', Object.keys(data));
              continue;
            }
          
          const form: PublicForm = {
            id: docSnap.id,
            title: data.title || '',
            ai: data.AI || false,  // Note the uppercase 'AI' to match Firestore field
            channelUid: data.channelUid || '',
            creator: data.creator || '',
            formImageUrl: data.formImageUrl || '',
            imageUrls: data.imageUrls || [],
            publicForm: data.publicForm || false,
            questions: data.questions || '',
            timestamp: data.timestamp,
            introMessage: data.introMessage,
            instructions: data.instructions,
            voiceEnabled: data.voiceEnabled || false,
            voiceDisclosure: data.voiceDisclosure,
          };

          console.log('✨ Form created successfully - id:', form.id, 'title:', form.title, 'channelUid:', form.channelUid, 'introMessage:', form.introMessage);
          return form;
          }
        } catch (pathError: any) {
          console.log('❌ Error checking path:', path, 'Error:', pathError.message);
        }
      }
      
      console.log('Form not found in any of the tried paths');
      return null;
    } catch (error: any) {
      console.error('Error getting public form:', error.message);
      console.error('Exception stack trace:', error.stack);
      return null;
    }
  }

  // Channel operations
  public async getChannel(path: string): Promise<Channel | null> {
    try {
      console.log('Fetching document from Firestore at path:', path);
      const docRef = doc(this.db, path);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        console.log('Document exists, converting to ChannelModel');
        const data = docSnap.data();
        const channel: Channel = {
          id: docSnap.id,
          name: data.name || '',
          description: data.description,
          createdAt: data.createdAt,
          updatedAt: data.updatedAt,
          userId: data.userId || '',
        };

        console.log('Channel details - name:', channel.name, 'userId:', channel.userId);
        return channel;
      } else {
        console.log('Document does not exist at path:', path);
        return null;
      }
    } catch (error: any) {
      console.error('Error getting channel:', error.message);
      console.error('Exception stack trace:', error.stack);
      return null;
    }
  }

  // Message operations
  public listenForMessages(formId: string, userId: string, callback: (messages: Message[]) => void): () => void {
    console.log('listenForMessages() called - formId:', formId, 'userId:', userId);
    
    // Try different paths for the messages collection
    const possiblePaths = [
      // Standard path
      ['public_forms', formId, 'activities', 'messages', userId],
      
      // If formId includes a prefix but the collection doesn't
      ...(formId.includes('Public_Form_') ? 
        [
          ['public_forms', formId.replace('Public_Form_', ''), 'activities', 'messages', userId]
        ] : []),
      
      // If formId doesn't include a prefix but the collection might
      ...(!formId.includes('Public_Form_') ? 
        [
          ['public_forms', `Public_Form_${formId}`, 'activities', 'messages', userId]
        ] : []),
        
      // Alternative paths
      ['forms', formId, 'messages', userId],
      ['forms', formId, 'user_messages', userId],
      ['public_forms', formId, 'user_messages', userId],
      ['channels', 'forms', formId, 'messages', userId]
    ];
    
    console.log('Trying these paths for message listening:', possiblePaths.map(p => p.join('/')));
    
    // We'll use this to keep track of all the unsubscribe functions
    const unsubscribeFunctions: (() => void)[] = [];
    
    // Create a unified set of messages from all sources
    const allMessages = new Map<string, Message>();
    
    // Helper function to update the callback with merged messages
    const updateCallback = () => {
      const mergedMessages = Array.from(allMessages.values())
        .sort((a, b) => {
          // Sort by timestamp if available
          if (a.timestamp && b.timestamp) {
            return a.timestamp.seconds - b.timestamp.seconds;
          }
          return 0;
        });
      
      console.log('Sending merged', mergedMessages.length, 'messages to callback');
      callback(mergedMessages);
    };
    
    // Try to listen on each path and also for user_messages subcollection
    possiblePaths.forEach((path, index) => {
      // First try the direct path
      try {
        console.log('Attempting to listen on direct path:', path.join('/'));
        const messagesRef = getCollectionRef(this.db, path);
        const q = query(messagesRef, orderBy('timestamp', 'asc'));
        
        const unsubscribe = onSnapshot(
          q,
          (snapshot: QuerySnapshot<DocumentData>) => {
            console.log(`Received snapshot from direct path ${index} with`, snapshot.docs.length, 'messages');
            
            // Handle document changes (added, modified, removed)
            snapshot.docChanges().forEach(change => {
              if (change.type === 'added' || change.type === 'modified') {
                const data = change.doc.data();
                allMessages.set(change.doc.id, {
                  id: change.doc.id,
                  messageText: data.messageText || '',
                  fromId: data.fromId || '',
                  toId: data.toId || '',
                  channelUid: data.channelUid || '',
                  timestamp: data.timestamp,
                  response: data.response,
                  formQuestions: data.formQuestions,
                  messageType: data.messageType,
                  paymentData: data.paymentData,
                  imageMessageUrl: data.imageMessageUrl,
                  choicePrompt: data.choicePrompt,
                  transferToFormId: data.transferToFormId,
                  transferToTitle: data.transferToTitle,
                  transferToImageUrl: data.transferToImageUrl,
                });
              } else if (change.type === 'removed') {
                console.log(`Removing deleted message: ${change.doc.id}`);
                allMessages.delete(change.doc.id);
              }
            });
            
            updateCallback();
          },
          (error: any) => {
            console.error(`Error listening for messages on direct path ${index}:`, error.message);
          }
        );
        
        unsubscribeFunctions.push(unsubscribe);
      } catch (error: any) {
        console.error(`Error setting up listener for direct path ${index}:`, error.message);
      }
      
      // Now try the user_messages subcollection
      try {
        const userMessagesPath = [...path, 'user_messages'];
        console.log('Attempting to listen on user_messages path:', userMessagesPath.join('/'));
        
        const userMessagesRef = getCollectionRef(this.db, userMessagesPath);
        const userMessagesQuery = query(userMessagesRef, orderBy('timestamp', 'asc'));
        
        const userMessagesUnsubscribe = onSnapshot(
          userMessagesQuery,
          (snapshot: QuerySnapshot<DocumentData>) => {
            console.log(`Received snapshot from user_messages path ${index} with`, snapshot.docs.length, 'messages');
            
            // Handle document changes (added, modified, removed)
            snapshot.docChanges().forEach(change => {
              if (change.type === 'added' || change.type === 'modified') {
                const data = change.doc.data();
                allMessages.set(change.doc.id, {
                  id: change.doc.id,
                  messageText: data.messageText || '',
                  fromId: data.fromId || '',
                  toId: data.toId || '',
                  channelUid: data.channelUid || '',
                  timestamp: data.timestamp,
                  response: data.response,
                  formQuestions: data.formQuestions,
                  messageType: data.messageType,
                  paymentData: data.paymentData,
                  imageMessageUrl: data.imageMessageUrl,
                  choicePrompt: data.choicePrompt,
                  transferToFormId: data.transferToFormId,
                  transferToTitle: data.transferToTitle,
                  transferToImageUrl: data.transferToImageUrl,
                });
              } else if (change.type === 'removed') {
                console.log(`Removing deleted message from user_messages: ${change.doc.id}`);
                allMessages.delete(change.doc.id);
              }
            });
            
            updateCallback();
          },
          (error: any) => {
            console.error(`Error listening for messages on user_messages path ${index}:`, error.message);
          }
        );
        
        unsubscribeFunctions.push(userMessagesUnsubscribe);
      } catch (error: any) {
        console.error(`Error setting up listener for user_messages path ${index}:`, error.message);
      }
    });
    
    // Also listen to form_manager_public for messages
    try {
      console.log('Attempting to listen on form_manager_public');
      const formManagerRef = collection(this.db, 'form_manager_public');
      const formManagerQuery = query(
        formManagerRef, 
        orderBy('timestamp', 'asc')
      );
      
      const formManagerUnsubscribe = onSnapshot(
        formManagerQuery,
        (snapshot: QuerySnapshot<DocumentData>) => {
          console.log(`Received snapshot from form_manager_public with`, snapshot.docs.length, 'messages');
          
          // Handle document changes (added, modified, removed)
          snapshot.docChanges().forEach(change => {
            const data = change.doc.data();
            const isRelevant = data.toId === formId && data.fromId === userId;
            
            if (change.type === 'added' || change.type === 'modified') {
              if (isRelevant) {
                allMessages.set(change.doc.id, {
                  id: change.doc.id,
                  messageText: data.messageText || '',
                  fromId: data.fromId || '',
                  toId: data.toId || '',
                  channelUid: data.channelUid || '',
                  timestamp: data.timestamp,
                  response: data.response,
                  formQuestions: data.formQuestions,
                  messageType: data.messageType,
                  paymentData: data.paymentData,
                  imageMessageUrl: data.imageMessageUrl,
                  choicePrompt: data.choicePrompt,
                  transferToFormId: data.transferToFormId,
                  transferToTitle: data.transferToTitle,
                  transferToImageUrl: data.transferToImageUrl,
                });
              }
            } else if (change.type === 'removed') {
              if (isRelevant) {
                console.log(`Removing deleted message from form_manager_public: ${change.doc.id}`);
                allMessages.delete(change.doc.id);
              }
            }
          });
          
          updateCallback();
        },
        (error: any) => {
          console.error(`Error listening for messages on form_manager_public:`, error.message);
        }
      );
      
      unsubscribeFunctions.push(formManagerUnsubscribe);
    } catch (error: any) {
      console.error(`Error setting up listener for form_manager_public:`, error.message);
    }
    
    // Return a function that unsubscribes from all listeners
    return () => {
      unsubscribeFunctions.forEach(unsubscribe => unsubscribe());
    };
  }

  public async sendMessage(message: Omit<Message, 'id'>): Promise<string> {
    console.log('sendMessage() called');
    console.log('Message details - fromId:', message.fromId, 'toId:', message.toId, 'text:', message.messageText);
    console.log('Message object:', JSON.stringify(message));
    
    try {
      // Try multiple collection paths - this is important since the form ID might not match the collection structure
      const possiblePaths = [
        // Standard path
        ['public_forms', message.toId, 'activities', 'messages', message.fromId],
        
        // If the form ID includes a prefix but the collection doesn't
        ...(message.toId.includes('Public_Form_') ? 
          [
            ['public_forms', message.toId.replace('Public_Form_', ''), 'activities', 'messages', message.fromId]
          ] : []),
        
        // If the form ID doesn't include a prefix but the collection might
        ...(!message.toId.includes('Public_Form_') ? 
          [
            ['public_forms', `Public_Form_${message.toId}`, 'activities', 'messages', message.fromId]
          ] : []),
          
        // Alternative path used in some implementations
        ['forms', message.toId, 'messages', message.fromId],
        
        // More paths to try
        ['forms', message.toId, 'user_messages', message.fromId],
        ['public_forms', message.toId, 'user_messages', message.fromId],
        ['channels', 'forms', message.toId, 'messages', message.fromId]
      ];
      
      console.log('All possible paths to try:', possiblePaths.map(p => p.join('/')));
      
      let result = null;
      let success = false;
      
      // Try each path until one works
      for (const path of possiblePaths) {
        console.log('Trying to send message to path:', path.join('/'));
        try {
          const messagesRef = getCollectionRef(this.db, path);
          
          const messageData: any = {
            messageText: message.messageText,
            fromId: message.fromId,
            toId: message.toId,
            channelUid: message.channelUid,
            timestamp: message.timestamp || Timestamp.now(),
            response: message.response,
            formQuestions: message.formQuestions,
          };
          if (message.messageType !== undefined) {
            messageData.messageType = message.messageType;
          }
          if (message.imageMessageUrl !== undefined) {
            messageData.imageMessageUrl = message.imageMessageUrl;
          }

          console.log('Sending message data:', JSON.stringify(messageData));
          result = await addDoc(messagesRef, messageData);
          console.log('Message sent successfully to path:', path.join('/'), 'with ID:', result.id);
          success = true;
          break;
        } catch (pathError: any) {
          console.log('Failed to send message to path:', path.join('/'), 'Error:', pathError.message);
          console.log('Error stack:', pathError.stack);
          // Continue to try the next path
        }
      }
      
      if (!success) {
        throw new Error('Failed to send message to any of the attempted paths');
      }
      
      // Also try to send to form_manager_public for tracking
      try {
        const formManagerRef = collection(this.db, 'form_manager_public');
        
        await addDoc(formManagerRef, {
          messageText: message.messageText,
          fromId: message.fromId,
          toId: message.toId,
          channelUid: message.channelUid,
          timestamp: message.timestamp || Timestamp.now(),
          response: message.response,
          formQuestions: message.formQuestions,
        });
        
        console.log('Message sent to form_manager_public');
      } catch (managerError: any) {
        // Don't fail if the secondary path fails
        console.log('Failed to send message to form_manager_public:', managerError.message);
      }
      
      return result!.id;
    } catch (error: any) {
      console.error('Error sending message:', error.message);
      throw error;
    }
  }

  public async sendFormMessage(formId: string, message: Message): Promise<void> {
    console.log('sendFormMessage() called');
    console.log('Form message details - formId:', formId, 'fromId:', message.fromId, 'messageId:', message.id);
    
    if (!message.id) {
      throw new Error('Message must have an id');
    }
    
    try {
      // From the Firestore structure we can see that messages are stored directly under:
      // public_forms → formId → activities → messages → userId → messageId
      // This is our primary path for sending messages
      
      // Prepare message data
      const messageData: any = {
        messageText: message.messageText,
        fromId: message.fromId,
        toId: message.toId,
        channelUid: message.channelUid,
        timestamp: message.timestamp || Timestamp.now(),
        id: message.id,
      };

      // Only include optional fields if they have values
      if (message.response !== undefined) {
        messageData.response = message.response;
      }
      if (message.formQuestions !== undefined) {
        messageData.formQuestions = message.formQuestions;
      }
      if (message.messageType !== undefined) {
        messageData.messageType = message.messageType;
      }
      if (message.imageMessageUrl !== undefined) {
        messageData.imageMessageUrl = message.imageMessageUrl;
      }

      // Only use the primary path
      // For intro messages, fromId is the form ID, so we need to use toId (the user ID) for the path
      const isIntroMessage = message.id.startsWith('intro_');
      const userIdForPath = isIntroMessage ? message.toId : message.fromId;
      
      const path = ['public_forms', formId, 'activities', 'messages', userIdForPath, message.id];
      console.log('Sending form message to primary path:', path.join('/'));
      console.log('Message type:', isIntroMessage ? 'intro message' : 'regular message', 'userIdForPath:', userIdForPath);
      
      // Create the document reference and set the data
      const docRef = getDocRef(this.db, path);
      await setDoc(docRef, messageData);
      
      console.log('Form message sent successfully to primary path with ID:', message.id);
      
      // Also send to form_manager_public for tracking
      try {
        const formManagerRef = getDocRef(this.db, ['form_manager_public', message.id]);
        await setDoc(formManagerRef, messageData);
        console.log('Form message sent to form_manager_public with ID:', message.id);
      } catch (managerError: any) {
        // Don't fail if the secondary path fails
        console.log('Failed to send form message to form_manager_public:', managerError.message);
      }
    } catch (error: any) {
      console.error('Error sending form message:', error.message);
      throw error;
    }
  }
  
  public async removeAllMessages(formId: string, userId: string): Promise<void> {
    console.log('removeAllMessages() called - formId:', formId, 'userId:', userId);
    
    try {
      // Use the same paths that listenForMessages uses to find messages
      const possiblePaths = [
        // Standard path
        ['public_forms', formId, 'activities', 'messages', userId],
        
        // If formId includes a prefix but the collection doesn't
        ...(formId.includes('Public_Form_') ? 
          [
            ['public_forms', formId.replace('Public_Form_', ''), 'activities', 'messages', userId]
          ] : []),
        
        // If formId doesn't include a prefix but the collection might
        ...(!formId.includes('Public_Form_') ? 
          [
            ['public_forms', `Public_Form_${formId}`, 'activities', 'messages', userId]
          ] : []),
          
        // Alternative paths
        ['forms', formId, 'messages', userId],
        ['forms', formId, 'user_messages', userId],
        ['public_forms', formId, 'user_messages', userId],
        ['channels', 'forms', formId, 'messages', userId]
      ];
      
      let totalDeleted = 0;
      
      // Try each path and delete any messages found
      for (const path of possiblePaths) {
        try {
          console.log('Checking for messages to delete at path:', path.join('/'));
          const messagesRef = getCollectionRef(this.db, path);
          const snapshot = await getDocs(messagesRef);
          
          if (snapshot.docs.length > 0) {
            console.log('Found', snapshot.docs.length, 'messages at path:', path.join('/'));
            
            for (const document of snapshot.docs) {
              console.log('Deleting message with ID:', document.id);
              await deleteDoc(document.ref);
              totalDeleted++;
            }
          }
        } catch (pathError: any) {
          console.log('No messages found at path:', path.join('/'), 'Error:', pathError.message);
        }
      }
      
      console.log('Successfully deleted', totalDeleted, 'total messages across all paths');
    } catch (error: any) {
      console.error('Error removing messages:', error.message);
      console.error('Exception stack trace:', error.stack);
      throw error;
    }
  }

  // JSON parsing for questions
  public parseQuestions(questionsString: string): Record<string, any> {
    console.log('parseQuestions() called');
    console.log('Input string length:', questionsString.length);
    
    try {
      console.log('Creating JSON object from input string');
      const result: Record<string, any> = {};
      const jsonObject = JSON.parse(questionsString);
      console.log('Successfully created JSON object');
      
      if (jsonObject.Required && jsonObject.Optional) {
        console.log('Detected Required/Optional format');
        const requiredObj = jsonObject.Required;
        const optionalObj = jsonObject.Optional;
        
        console.log('Processing Required section with', Object.keys(requiredObj).length, 'keys');
        result.Required = Object.keys(requiredObj).reduce((acc, key) => {
          acc[key] = requiredObj[key].toString();
          return acc;
        }, {} as Record<string, string>);
        
        console.log('Processing Optional section with', Object.keys(optionalObj).length, 'keys');
        result.Optional = Object.keys(optionalObj).reduce((acc, key) => {
          acc[key] = optionalObj[key].toString();
          return acc;
        }, {} as Record<string, string>);
      } else {
        // Check if it's a map of section names to question maps
        console.log('Checking if JSON is a map of maps');
        let isMapOfMaps = true;
        
        for (const key in jsonObject) {
          if (typeof jsonObject[key] !== 'object' || jsonObject[key] === null) {
            console.log('Found non-object value for key:', key, 'not a map of maps');
            isMapOfMaps = false;
            break;
          }
        }
        
        if (isMapOfMaps) {
          console.log('Detected JSON as map of maps format');
          for (const sectionKey in jsonObject) {
            console.log('Processing section:', sectionKey);
            const sectionObj = jsonObject[sectionKey];
            console.log('Section', sectionKey, 'has', Object.keys(sectionObj).length, 'keys');
            
            result[sectionKey] = Object.keys(sectionObj).reduce((acc, key) => {
              acc[key] = sectionObj[key].toString();
              return acc;
            }, {} as Record<string, string>);
          }
        } else {
          // Treat as a flat map of questions
          console.log('Treating JSON as flat map of questions');
          console.log('Processing', Object.keys(jsonObject).length, 'keys');
          
          result.Questions = Object.keys(jsonObject).reduce((acc, key) => {
            acc[key] = jsonObject[key].toString();
            return acc;
          }, {} as Record<string, string>);
        }
      }
      
      console.log('Successfully parsed questions into', Object.keys(result).length, 'sections');
      return result;
    } catch (error: any) {
      console.error('Error parsing questions:', error.message);
      console.error('Exception stack trace:', error.stack);
      console.log('Returning empty object due to parsing error');
      return {};
    }
  }
}

export default FirestoreRepository;